import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface ModelRow {
  readonly modelId: string;
  readonly modelCode: string;
  readonly modelName: string;
  readonly modelType: string;
  readonly useCaseCategory: string;
  readonly status: string;
  readonly approvalStatus: string;
  readonly isCritical: boolean;
  /** Needed to honour BR-02 (segregation of duties) in the UI, not only the API. */
  readonly ownerId?: string | null;
}

const MODEL_STATUSES = ['', 'DRAFT', 'PENDING_APPROVAL', 'ACTIVE', 'SUSPENDED', 'RETIRED'];
const MODEL_TYPES = ['PREDICTION', 'FORECAST', 'RISK_SCORING', 'FRAUD_DETECTION', 'ANOMALY_DETECTION', 'RECOMMENDATION', 'ASSIGNMENT', 'CLASSIFICATION'];
const USE_CASE_CATEGORIES = ['', 'OPERATIONAL', 'FINANCIAL', 'COMPLIANCE', 'MANAGEMENT'];

/**
 * M25 — Model Registry (§7).
 * Catalogue of every AI model with its lifecycle status, maker-checker approval
 * state and criticality flag. Versions are immutable and auditable (BR-04);
 * this registry is the single source of truth for governed AI assets.
 */
export function M25ModelRegistryPage(): ReactNode {
  const { t: tr } = useTranslation();
  const { token, user } = useAuth();
  const [rows, setRows] = useState<ModelRow[]>([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [modelName, setModelName] = useState('');
  const [modelType, setModelType] = useState('PREDICTION');
  const [useCaseCategory, setUseCaseCategory] = useState('');

  const load = useCallback((): void => {
    setLoading(true);
    const query = status ? `?status=${encodeURIComponent(status)}` : '';
    apiRequest<{ data: ModelRow[]; total: number }>(`/v1/ai/models${query}`, { token })
      .then((r) => { setRows(r.data); setTotal(r.total); setError(null); })
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, [token, status]);

  useEffect(() => { load(); }, [load]);

  function onRegister(e: FormEvent): void {
    e.preventDefault();
    setBusy(true);
    setNotice(null);
    setError(null);
    apiRequest('/v1/ai/models', {
      method: 'POST',
      token,
      body: { modelName, modelType, useCaseCategory: useCaseCategory || undefined },
    })
      .then(() => { setNotice(tr('m25.registry.modelRegistered')); setModelName(''); load(); })
      .catch((err) => setError((err as Error).message))
      .finally(() => setBusy(false));
  }

  function act(modelId: string, action: 'submit' | 'approve'): void {
    setBusy(true);
    setNotice(null);
    setError(null);
    apiRequest(`/v1/ai/models/${encodeURIComponent(modelId)}/${action}`, { method: 'POST', token })
      .then(() => { setNotice(action === 'submit' ? tr('m25.registry.modelSubmitted') : tr('m25.registry.modelApproved')); load(); })
      .catch((e) => setError((e as Error).message))
      .finally(() => setBusy(false));
  }

  // The same two rules ModelService enforces, so the screen never offers an
  // action the API is bound to refuse with a 422:
  //   submit  — only from DRAFT or REJECTED
  //   approve — only from SUBMITTED, and never by the model's own owner (BR-02,
  //             segregation of duties)
  const canSubmit = (r: ModelRow): boolean =>
    r.approvalStatus === 'DRAFT' || r.approvalStatus === 'REJECTED';
  const canApprove = (r: ModelRow): boolean =>
    r.approvalStatus === 'SUBMITTED' && !!user?.id && r.ownerId !== user.id;

  return (
    <main aria-label="model-registry">
      <h1>{tr('m25.registry.title')}</h1>
      <form aria-label="model-create-form" onSubmit={onRegister}>
        <label htmlFor="model-name">{tr('m25.registry.modelName')}</label>
        <input id="model-name" value={modelName} onChange={(e) => setModelName(e.target.value)} required />
        <label htmlFor="model-type">{tr('m25.registry.modelType')}</label>
        <select id="model-type" value={modelType} onChange={(e) => setModelType(e.target.value)}>
          {MODEL_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <label htmlFor="model-use-case">{tr('m25.registry.useCase')}</label>
        <select id="model-use-case" value={useCaseCategory} onChange={(e) => setUseCaseCategory(e.target.value)}>
          {USE_CASE_CATEGORIES.map((c) => <option key={c || 'none'} value={c}>{c || '—'}</option>)}
        </select>
        <button type="submit" disabled={busy}>{tr('m25.registry.register')}</button>
      </form>
      <label htmlFor="model-status-filter">{tr('m25.registry.filterLabel')}</label>
      <select id="model-status-filter" value={status} onChange={(e) => setStatus(e.target.value)}>
        {MODEL_STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || tr('m25.registry.allStatuses')}</option>)}
      </select>
      {notice && <p role="status">{notice}</p>}
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{tr('m25.registry.loading')}</p> : (
        <>
          <p aria-label="model-count">{tr('m25.registry.count', { count: total })}</p>
          <table aria-label="model-table">
            <thead>
              <tr><th>{tr('m25.registry.colCode')}</th><th>{tr('m25.registry.colName')}</th><th>{tr('m25.registry.colType')}</th><th>{tr('m25.registry.colUseCase')}</th><th>{tr('m25.registry.colStatus')}</th><th>{tr('m25.registry.colApproval')}</th><th>{tr('m25.registry.colCritical')}</th><th>{tr('m25.registry.colActions')}</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.modelId}>
                  <td>{r.modelCode}</td>
                  <td>{r.modelName}</td>
                  <td>{r.modelType}</td>
                  <td>{r.useCaseCategory}</td>
                  <td>{r.status}</td>
                  <td>{r.approvalStatus}</td>
                  <td>{r.isCritical ? tr('m25.registry.yes') : tr('m25.registry.no')}</td>
                  <td>
                    <button type="button" aria-label={`submit-${r.modelId}`} disabled={busy || !canSubmit(r)} title={canSubmit(r) ? undefined : tr('m25.registry.submitUnavailable')} onClick={() => act(r.modelId, 'submit')}>{tr('m25.registry.submit')}</button>
                    <button type="button" aria-label={`approve-${r.modelId}`} disabled={busy || !canApprove(r)} title={canApprove(r) ? undefined : tr('m25.registry.approveUnavailable')} onClick={() => act(r.modelId, 'approve')}>{tr('m25.registry.approve')}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
